import { useBranchesQuery } from '../api/repositories/use-branches.query';
import { VehicleAccidentDtoPagedListData } from 'api/generated';
import { useAuth } from 'features/auth/contexts/auth';
import { useEmployeeDepartments } from 'features/employees/api/repositories/use-employee-departments.query';
import { useEmployeeJobTitlesQuery } from 'features/employees/api/repositories/use-employee-job-titles-query';
import { useVehicleAccidentQuery } from 'features/employees/api/repositories/use-employees.query';
import { useSchedulesHolidayRequestsNotApprovedCount } from 'features/holidays/api/repositories/use-attendence-schedules-holiday-requests-not-approved-count.query';
import React, { useEffect } from 'react';
import { SelectOptions } from 'ts-types';
import { splitPascalCase } from 'utils/strings';

export type AppStoreState = {
  departments: SelectOptions<number, string>[];
  branches: SelectOptions<number, string>[];
  jobTitles: SelectOptions<number, string>[];
  notApproved: number;
  accidentLogs: VehicleAccidentDtoPagedListData;
};

export type AppStoreContextProps = {
  state: AppStoreState;
  updateStore: (updates: Partial<AppStoreState>) => void;
  changeApprovedCount: (increment: number) => void;
};

export const initialQueryRequest: AppStoreContextProps = {
  state: {
    departments: [],
    branches: [],
    jobTitles: [],
    notApproved: 0,
    accidentLogs: {}
  },
  changeApprovedCount: (increment: number) => {
    // empty
  },
  updateStore: () => {
    // empty
  }
};

export const AppStoreContext = React.createContext<AppStoreContextProps>(initialQueryRequest);

AppStoreContext.displayName = 'AppStoreContext';

export const AppStoreProvider = ({ children }: { children: React.ReactNode }) => {
  const { currentUser } = useAuth();
  const [state, setState] = React.useState<AppStoreState>(initialQueryRequest.state);

  const updateStore = (updates: Partial<AppStoreState>) => {
    const updatedState = { ...state, ...updates } as AppStoreState;
    setState(updatedState);
  };

  const changeApprovedCount = (increment: number) => {
    setState((prevState) => ({
      ...prevState,
      notApproved: prevState.notApproved + increment
    }));
  };

  const enabled = !!currentUser;

  useBranchesQuery(undefined, {
    enabled,
    onSuccess: (data) => {
      const branches: SelectOptions<number, string>[] = data.map((item) => {
        return {
          label: splitPascalCase(item.name!),
          value: item.id!
        };
      });

      setState((prevState) => ({
        ...prevState,
        branches
      }));
    }
  });

  useEmployeeDepartments(undefined, {
    enabled,
    onSuccess: (loadedDepartments) => {
      const departments: SelectOptions<number, string>[] = [];
      for (const key in loadedDepartments) {
        departments.push({
          label: splitPascalCase(loadedDepartments[key]),
          value: Number(key)
        });
      }

      setState((prevState) => ({
        ...prevState,
        departments
      }));
    }
  });

  useSchedulesHolidayRequestsNotApprovedCount(undefined, {
    enabled,
    onSuccess: (notApproved) => {
      setState((prevState) => ({
        ...prevState,
        notApproved
      }));
    }
  });

  useEmployeeJobTitlesQuery(undefined, {
    enabled,
    onSuccess: (loadedJobTitles) => {
      const jobTitles: SelectOptions<number, string>[] = [];
      for (const key in loadedJobTitles) {
        jobTitles.push({
          label: splitPascalCase(loadedJobTitles[key]),
          value: Number(key)
        });
      }

      setState((prevState) => ({
        ...prevState,
        jobTitles
      }));
    }
  });
  useVehicleAccidentQuery(
    {
      skipPagination: false
    },
    undefined,
    {
      enabled: true,
      onSuccess: (loadedAccidentLogs) => {
        const accidentLogs = loadedAccidentLogs.data;
        setState((prevState) => ({
          ...prevState,
          accidentLogs: {
            items: accidentLogs,
            count: accidentLogs?.length
          }
        }));
      }
    }
  );

  return (
    <AppStoreContext.Provider value={{ state, updateStore, changeApprovedCount }}>{children}</AppStoreContext.Provider>
  );
};

export const useAppStore = () => {
  const context = React.useContext(AppStoreContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
